import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { io } from 'socket.io-client';
import { useAuth, api } from '../auth';
import { Icon } from './ui';

export default function NotificationBell() {
    const { user } = useAuth();
    const [notifs, setNotifs] = useState([]);
    const [unread, setUnread] = useState(0);
    const [bellOpen, setBellOpen] = useState(false);

    useEffect(() => {
        if (!user || !user.id && !user._id) return;

        // 1. Fetch initial DB state
        api.get('/notifications')
            .then(res => {
                setNotifs(res.data.notifications || []);
                setUnread(res.data.unread || 0);
            })
            .catch(err => console.error('[BELL_INIT]', err.message));

        // 2. Ironclad Socket Connection (Forcing credentials to match backend CORS)
        const socket = io('http://localhost:5000', {
            withCredentials: true,
            transports: ['websocket', 'polling']
        });

        // 3. Strict Connection Logging
        socket.on('connect', () => {
            console.log('✅ WebSocket Connected. ID:', socket.id);
            socket.emit('join', user.id || user._id);
        });

        socket.on('connect_error', (err) => {
            console.error('❌ WebSocket Connection Error:', err.message);
        });

        // 4. Catch the exact payload
        socket.on('notification', (newNotif) => {
            console.log('🔔 Live Notification Received:', newNotif);
            if (!newNotif || !newNotif._id) return;
            setNotifs(prev => [newNotif, ...prev]);
            setUnread(prev => prev + 1);
        });

        return () => socket.disconnect();
    }, [user]);

    const openBell = () => {
        setBellOpen(!bellOpen);
        if (!bellOpen && unread > 0) {
            setUnread(0);
            api.post('/notifications/read').catch(err => console.error(err));
        }
    };

    return (
        <div className="relative">
            <button onClick={openBell} className="relative p-2 rounded-lg hover:bg-surface-container-high transition-colors" title="Notifications">
                <Icon name="notifications" size={22} className="text-on-surface-variant" />
                {unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-error text-white text-[11px] font-bold rounded-full flex items-center justify-center">
                        {unread > 9 ? '9+' : unread}
                    </span>
                )}
            </button>

            {bellOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-surface border border-outline-variant rounded-lg shadow-lg overflow-hidden z-20">
                    <div className="px-md py-3 border-b border-outline-variant flex items-center justify-between bg-surface-container-lowest">
                        <span className="font-label-lg text-label-lg font-bold text-primary">Notifications</span>
                        <button onClick={() => setBellOpen(false)} className="text-on-surface-variant hover:text-error transition-colors">
                            <Icon name="close" size={18} />
                        </button>
                    </div>

                    <div className="max-h-80 overflow-y-auto divide-y divide-outline-variant">
                        {notifs.length === 0 ? (
                            <div className="px-md py-8 text-center text-on-surface-variant flex flex-col items-center">
                                <Icon name="notifications_off" size={32} className="mb-2 opacity-50" />
                                <p className="font-body-sm text-body-sm">No notifications yet.</p>
                            </div>
                        ) : notifs.map((n) => {
                            const isApproved = n.text?.toLowerCase().includes('granted') || n.text?.toLowerCase().includes('approved');
                            const isDenied = n.text?.toLowerCase().includes('revoked') || n.text?.toLowerCase().includes('denied');
                            const iconName = isApproved ? 'check_circle' : isDenied ? 'cancel' : 'pending_actions';
                            const iconColor = isApproved ? 'text-tertiary' : isDenied ? 'text-error' : 'text-secondary';

                            return (
                                <Link
                                    key={n._id}
                                    to={n.link || '#'}
                                    onClick={() => setBellOpen(false)}
                                    className={`flex gap-3 px-md py-3 transition-colors hover:bg-surface-container-high ${n.read ? 'bg-surface' : 'bg-secondary-container/20'}`}
                                >
                                    <div className="shrink-0 mt-0.5">
                                        <Icon name={iconName} size={20} className={iconColor} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-body-sm text-body-sm text-on-surface leading-snug">
                                            {n.text?.split('"').map((part, i) =>
                                                i % 2 === 1 ? <strong key={i} className="font-semibold text-primary">"{part}"</strong> : part
                                            )}
                                        </p>
                                        <p className="font-label-md text-label-md text-on-surface-variant mt-1.5 uppercase tracking-wider">
                                            {new Date(n.createdAt || Date.now()).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}